import Sidebar from "./Sidebar";
import { useEffect, useState, useCallback } from "react";
import MaterialTable from "@material-table/core";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const MySwal = withReactContent(Swal);
const API_URL = process.env.REACT_APP_API_URL;

/**
 * ========================= Helper Hooks =========================
 */
function useFetch(url) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch");
      const json = await res.json();
      setData(json);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, refetch: fetchData };
}

/**
 * ========================= API layer =========================
 */
const BlogAPI = {
  add: async (payload) => {
    const formData = buildFormData(payload);
    const res = await fetch(`${API_URL}/api/blog/blogPost`, {
      method: "POST",
      body: formData,
    });
    return handleApiResponse(res);
  },
  update: async (id, payload) => {
    const formData = buildFormData(payload);
    const res = await fetch(`${API_URL}/api/blog/updateblog/${id}`, {
      method: "PUT",
      body: formData,
    });
    return handleApiResponse(res);
  },
  delete: async (id) => {
    const res = await fetch(`${API_URL}/api/blog/dltblog/${id}`, {
      method: "DELETE",
    });
    return handleApiResponse(res);
  },
};

function buildFormData({ storyName, description, fullStory, image }) {
  const fd = new FormData();
  fd.append("storyName", storyName);
  fd.append("description", description);
  fd.append("fullStory", fullStory);
  if (image) fd.append("image", image);
  return fd;
}

async function handleApiResponse(res) {
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || "Unknown error");
  return json;
}

/**
 * ========================= Dialog =========================
 */
function openBlogDialog(initialData = {}, onSubmit) {
  let draft = {
    ...initialData,
    imageUrl: initialData.image || "",
    image: null,
  };
  const isEdit = Boolean(initialData._id);

  const Form = () => (
    <div id="swal-blog-form" className="flex flex-col gap-4 p-2 text-left">
      {[
        {
          label: "Story Name *",
          field: "storyName",
          as: "input",
        },
        {
          label: "Short Description *",
          field: "description",
          as: "textarea",
          rows: 3,
        },
      ].map(({ label, field, as, ...rest }) => {
        const Component = as;
        return (
          <Component
            key={field}
            defaultValue={draft[field] || ""}
            placeholder={label}
            className="border rounded-md p-2 text-sm resize-none focus:ring-2 ring-blue-300 outline-none"
            {...rest}
            onChange={(e) => (draft[field] = e.target.value.trim())}
          />
        );
      })}

      {/* ✅ FULL STORY WITH QUILL */}
      <div>
        <label className="block mb-1 text-sm font-medium">Full Story *</label>
        <ReactQuill
          defaultValue={draft.fullStory || ""}
          onChange={(value) => (draft.fullStory = value)}
          className="bg-white"
          theme="snow"
        />
      </div>

      <input
        type="file"
        accept="image/*"
        onChange={(e) => {
          if (e.target.files.length) {
            draft.image = e.target.files[0];
            const reader = new FileReader();
            reader.onload = () => {
              const img = document.getElementById("blog-img-preview");
              img.src = reader.result;
              img.style.display = "block";
            };
            reader.readAsDataURL(draft.image);
          }
        }}
        className="file:mr-3 file:py-1 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200"
      />
      <img
        id="blog-img-preview"
        src={draft.imageUrl || ""}
        alt="Preview"
        className={`${
          draft.imageUrl ? "block" : "hidden"
        } max-w-full max-h-48 rounded-md mt-2 border shadow-sm object-cover`}
      />
    </div>
  );

  MySwal.fire({
    title: `<h2 class="text-xl font-semibold text-gray-800">${
      isEdit ? "Edit Story" : "Add New Story"
    }</h2>`,
    html: <Form />,
    showCancelButton: true,
    confirmButtonText: isEdit ? "Update" : "Submit",
    cancelButtonText: "Cancel",
    width: "42rem",
    didOpen: () => {
      const content = document.getElementById("swal-blog-form");
      const container = Swal.getPopup().querySelector(".swal2-html-container");
      container.innerHTML = "";
      container.appendChild(content);
    },
    preConfirm: () => {
      if (!draft.storyName || !draft.description || !draft.fullStory) {
        Swal.showValidationMessage("Please fill in all required fields.");
        return false;
      }
      if (!draft.image) {
        draft.image = null;
      }
      return draft;
    },
  }).then((r) => {
    if (r.isConfirmed) onSubmit(r.value, isEdit);
  });
}

/**
 * ========================= Main Component =========================
 */
export default function BlogRegister() {
  const { data: blogs, refetch } = useFetch(`${API_URL}/api/blog/getAllblogs`);

  const addBlog = async (payload) => {
    try {
      await BlogAPI.add(payload);
      Swal.fire("Success", "Blog posted!", "success");
      refetch();
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    }
  };

  const updateBlog = async (payload) => {
    try {
      await BlogAPI.update(payload._id, payload);
      Swal.fire("Success", "Blog updated!", "success");
      refetch();
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    }
  };

  const deleteBlog = async (id, storyName) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete "${storyName}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await BlogAPI.delete(id);
        Swal.fire("Deleted!", "Your blog has been deleted.", "success");
        refetch();
      } catch (e) {
        Swal.fire("Error", e.message, "error");
      }
    }
  };

  return (
    <div className="w-screen pt-[2%] px-[1%] h-[100vh] bg-[#f1f2f6] flex flex-row">
      <Sidebar />
      <div className="flex pl-5 w-full">
        <div className="w-[80vw] h-[90vh] overflow-y-auto">
          <MaterialTable
            title="Blog Stories"
            columns={[
              {
                title: "Heading",
                field: "storyName",
                render: (row) => (
                  <span className="font-semibold text-md">{row.storyName}</span>
                ),
              },
              {
                title: "Image",
                field: "image",
                render: (row) => (
                  <img
                    src={row.image}
                    alt={row.storyName}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                ),
              },
              {
                title: "Description",
                field: "description",
                render: (row) => (
                  <span className="text-sm text-gray-700">
                    {row.description}
                  </span>
                ),
              },
            ]}
            data={blogs}
            options={{
              pageSize: 5,
              pageSizeOptions: [5, 10, 20],
              toolbar: true,
              paging: true,
            }}
            actions={[
              {
                icon: () => <EditIcon />,
                tooltip: "Edit Story",
                onClick: (_, row) => openBlogDialog(row, updateBlog),
              },
              {
                icon: () => <AddIcon />,
                tooltip: "Add Story",
                isFreeAction: true,
                onClick: () => openBlogDialog({}, addBlog),
              },
              {
                icon: () => <DeleteIcon color="error" />,
                tooltip: "Delete Story",
                onClick: (_, row) => deleteBlog(row._id, row.storyName),
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
}
